import { useQuery } from '@tanstack/react-query';
import usePhotosViewCollectionPermissionEnabled from 'containers/photos/hooks/usPhotosViewCollectionPermissionEnabled/usePhotosViewCollectionPermissionEnabled';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import sumBy from 'lodash/sumBy';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import ITimeRange from 'types/TimeRange';
import {
  IPhotoAndNotesCounts,
  PHOTO_AND_NOTES_COUNTS_QUERY,
  toCreatedOnBound,
} from '../photos-and-notes-dashboard-card-query';

export default function useDashboardPhotos(
  timeRange: ITimeRange<DateTime>,
  ranges: Array<ITimeRange<number>>,
  memberId?: string,
  projectId?: string,
  enabled: boolean = true
) {
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();
  const requirePermission = usePhotosViewCollectionPermissionEnabled();

  const rangesKey = JSON.stringify(ranges);
  const serverRanges = useMemo(
    () =>
      ranges.map((range) => ({
        startTime: toCreatedOnBound(DateTime.fromSeconds(range.startTime, { zone: 'utc' })),
        endTime: toCreatedOnBound(DateTime.fromSeconds(range.endTime, { zone: 'utc' })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangesKey]
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...baseKey, 'photos-and-notes-dashboard', { rangesKey, memberId, projectId, requirePermission }],
    queryFn: async () => {
      const result = await graphQLClient.request<{ dashboard: { photoAndNotesCounts: IPhotoAndNotesCounts } }>({
        document: PHOTO_AND_NOTES_COUNTS_QUERY,
        variables: {
          ranges: serverRanges,
          memberId: memberId ?? null,
          projectId: projectId ?? null,
          requirePermission,
          includeFooter: false,
        },
      });
      return result.dashboard.photoAndNotesCounts.countsByRange;
    },
    enabled,
  });

  const chartData = ranges.map((range, index) => ({ day: range.startTime, value: data?.[index]?.count ?? 0 }));

  return {
    data: chartData,
    total: sumBy(chartData, (datum) => datum.value),
    loading: isLoading,
    error: error ?? null,
    execute: refetch,
  };
}
